import type { SkyState } from "./eclipse";

const STAR_COUNT = 90;
const stars: { x: number; y: number; r: number; twinkle: number }[] = Array.from(
  { length: STAR_COUNT },
  () => ({
    x: Math.random(),
    y: Math.random(),
    r: Math.random() * 1.2 + 0.2,
    twinkle: Math.random() * Math.PI * 2,
  })
);

export function drawSky(canvas: HTMLCanvasElement, sky: SkyState, tSec: number): void {
  const dpr = window.devicePixelRatio || 1;
  const cssSize = canvas.clientWidth;
  if (canvas.width !== cssSize * dpr) {
    canvas.width = cssSize * dpr;
    canvas.height = cssSize * dpr;
  }
  const ctx = canvas.getContext("2d");
  if (!ctx) return;
  ctx.save();
  ctx.scale(dpr, dpr);
  const size = cssSize;
  const cx = size / 2;
  const cy = size / 2;

  // Sky darkens as obscuration rises; stars fade in near totality.
  const dark = sky.sunUp ? Math.min(1, sky.obscurationPercent / 100) ** 2.2 : 1;
  const skyTop = lerpColor([11, 13, 23], [2, 2, 6], dark);
  const skyBottom = lerpColor([21, 24, 41], [4, 4, 10], dark);
  const grad = ctx.createLinearGradient(0, 0, 0, size);
  grad.addColorStop(0, rgb(skyTop));
  grad.addColorStop(1, rgb(skyBottom));
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);

  if (!sky.sunUp) {
    ctx.fillStyle = "rgba(226,224,255,0.65)";
    ctx.font = "500 15px 'Inter', sans-serif";
    ctx.textAlign = "center";
    ctx.fillText("Sun is below the horizon", cx, cy);
    ctx.restore();
    return;
  }

  const starAlpha = dark;
  if (starAlpha > 0.02) {
    for (const s of stars) {
      const tw = 0.55 + 0.45 * Math.sin(tSec * 1.6 + s.twinkle);
      ctx.fillStyle = `rgba(240,240,255,${(starAlpha * tw * 0.9).toFixed(3)})`;
      ctx.beginPath();
      ctx.arc(s.x * size, s.y * size, s.r, 0, Math.PI * 2);
      ctx.fill();
    }
  }

  // Degrees -> pixels scale: keep the sun a fixed, legible size on screen.
  const sunPxRadius = size * 0.16;
  const degToPx = sunPxRadius / sky.sunRadiusDeg;

  const moonPxRadius = sky.moonRadiusDeg * degToPx;
  const moonX = cx + sky.dxDeg * degToPx;
  const moonY = cy - sky.dyDeg * degToPx; // screen y grows downward; sky altitude grows upward

  // Corona glow behind the sun, intensifying visually near totality.
  const coronaBoost = 1 + dark * 1.8;
  const coronaR = sunPxRadius * (2.4 * coronaBoost);
  const corona = ctx.createRadialGradient(cx, cy, sunPxRadius * 0.6, cx, cy, coronaR);
  corona.addColorStop(0, "rgba(255,208,128,0.55)");
  corona.addColorStop(0.35, "rgba(255,180,84,0.22)");
  corona.addColorStop(1, "rgba(255,180,84,0)");
  ctx.fillStyle = corona;
  ctx.beginPath();
  ctx.arc(cx, cy, coronaR, 0, Math.PI * 2);
  ctx.fill();

  // Sun disc.
  const sunGrad = ctx.createRadialGradient(cx, cy, 0, cx, cy, sunPxRadius);
  sunGrad.addColorStop(0, "#fff6df");
  sunGrad.addColorStop(0.7, "#ffd77a");
  sunGrad.addColorStop(1, "#f0a848");
  ctx.fillStyle = sunGrad;
  ctx.beginPath();
  ctx.arc(cx, cy, sunPxRadius, 0, Math.PI * 2);
  ctx.fill();

  // Moon disc (occluding).
  ctx.fillStyle = "#050510";
  ctx.beginPath();
  ctx.arc(moonX, moonY, moonPxRadius, 0, Math.PI * 2);
  ctx.fill();

  // Thin limb highlight on the moon so it reads as a sphere, not a hole.
  ctx.strokeStyle = "rgba(120,120,150,0.35)";
  ctx.lineWidth = 1;
  ctx.beginPath();
  ctx.arc(moonX, moonY, moonPxRadius, 0, Math.PI * 2);
  ctx.stroke();

  ctx.restore();
}

function lerpColor(a: number[], b: number[], t: number): number[] {
  return a.map((v, i) => v + (b[i] - v) * t);
}

function rgb([r, g, b]: number[]): string {
  return `rgb(${r | 0},${g | 0},${b | 0})`;
}
